import React, {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  memo,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  WheelEvent,
} from 'react';
import { Control, Controller, SubmitHandler, useForm, UseFormSetValue } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { Subscription } from 'react-hook-form/dist/utils/createSubject';
import { useTranslation } from 'next-i18next';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Divider, Form, Input as AntInput, Radio, Select, Space, Switch, Typography } from 'antd';
import { nanoid } from 'nanoid';
import { FilterFunc } from 'rc-select/es/Select';
import { Tooltip } from 'src/shared/components/tooltip';
import styled from 'styled-components';

import { checkCondition2 } from '@/shared/_legacy_/condition-utils';
import { Button, Dropdown, Input } from '@/shared/components';
import { Row } from '@/shared/components/layout';
import { PhoneCodesListForm, resolveCountry } from '@/shared/constants/countries-list';
import {
  FormItemLabelType,
  FormLabelItem,
  isLabels,
  PickDtoFilter,
  RenderNewButtonDeleteOptions,
  TDtoWrapper,
  ValueType,
} from '@/shared/lib/forms/form-wrapper.types';
import { toDto } from '@/shared/lib/hooks/use-form-render/format-functions';
import { UseFormRenderInputType } from '@/shared/lib/hooks/use-form-render/types';
import { CollectionName, ICollections } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { log } from '@/shared/utils/log';
import { isEmpty } from '@/shared/utils/misc';

import { FileUploading } from './components/FileUploading';
import { PickArrayOfDto } from './components/PickArrayOfDto';
import { SelectPidBeneficiary } from './components/SelectPidBeneficiary';
import { separator, TextAreaDefaultRowsCount } from './constants';
import { EMAIL_REGEX } from './helpers';
import { DefaultInputToInputType, isDefaultInput } from './types';

type ValidateErrorEntity = {
  errorFields: {
    name: (string | number)[];
    errors: string[];
  }[];
};

const ADD_NEW_PREFIX = 'add-new';

export const FAST_ADD_COMMON_ITEM = 'COMMON';

export const useFormRender = <
  TDto extends Omit<BaseDto, 'id'>,
  THelp extends { [TKey in keyof TDto]: QuestionType },
>({
  form,
  texts = {},
  onSubmit: onExternalSubmit,
  // onDelegationSubmit: onExternalDelegationSubmit,
  defaultValues = {},
  onSave,
  isLastStep,
  // withDelegation = false,
  withSaveButton = true,
  validateOnMount = false, // removeAllRequired,
}: UseFormRenderInputType<TDto, THelp> & { onSave?: (data: TDto) => void }) => {
  const {
    control,
    handleSubmit,
    watch,
    setError,
    getValues,
    formState: { isDirty },
    setValue,
    reset,
    trigger,
  } = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (validateOnMount) {
      queueMicrotask(trigger);
    }
  }, []);

  const isNewInited = useRef(false);

  const _form = new Proxy(form, {
    set(item, prop, value) {
      // eslint-disable-next-line security/detect-object-injection,@typescript-eslint/ban-ts-comment
      // @ts-ignore
      item[prop] = value;
      setForceRerender((prev) => ({ ...prev, ...{} }));
      return true;
    },
  });

  const memoizedValue = useRef<Record<string, unknown>>(defaultValues);
  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (
      (defaultValues as { [ADD_NEW_PREFIX]?: Record<string, unknown> })[ADD_NEW_PREFIX] &&
      !isNewInited.current
    ) {
      isNewInited.current = true;

      for (const [key, item] of Object.entries(defaultValues[ADD_NEW_PREFIX])) {
        for (const [_key, it] of Object.entries(item as Record<string, unknown>)) {
          for (const [k, v] of Object.entries(it as Record<string, unknown>)) {
            if (!isEmpty(v)) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              _form[`${ADD_NEW_PREFIX}${separator}${key}${separator}${k}${separator}${_key}`] =
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                _form[k];
              memoizedValue.current[
                `${ADD_NEW_PREFIX}${separator}${key}${separator}${k}${separator}${_key}`
              ] = v;
            }
          }
        }
      }
    }
  }, [defaultValues, isNewInited.current]);

  useEffect(() => {
    for (const i of Object.keys(form)) {
      if (i.startsWith(ADD_NEW_PREFIX) && isEmpty(memoizedValue.current[i], true)) {
        delete form[i as keyof typeof form];
        delete memoizedValue.current[i];
      }
    }
  }, [form]);

  const [disabled, setDisabled] = useState<boolean>(false);

  const entityType = watch(defaultValues).entityType;

  useEffect(() => {
    if (entityType === 'passive') {
      setDisabled(true);
    } else setDisabled(false);
  }, [entityType]);

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    if (isLastStep === undefined || isLastStep) {
      setDisabled(true);
    }
    memoizedValue.current = { ...memoizedValue.current, ...toDto(data) };
    onExternalSubmit?.(toDto(data));
  };

  const onSaveSubmit: SubmitHandler<FieldValues> = (data) => {
    memoizedValue.current = { ...memoizedValue.current, ...toDto(data) };
    onSave?.(toDto(data));
  };

  // const onDelegationSubmit: SubmitHandler<FieldValues> = (data) => {
  //   if (isLastStep === undefined || isLastStep) {
  //     setDisabled(true);
  //   }
  //   memoizedValue.current = { ...memoizedValue.current, ...toDto(data) };
  //   onExternalDelegationSubmit?.(toDto(data));
  // };

  const [forceReRender, setForceRerender] = useState<Record<string, unknown>>(defaultValues);

  useEffect(() => {
    // eslint-disable-next-line no-void
    return () => void (memoizedValue.current = {});
  }, []);

  useEffect(() => {
    let subscription: Subscription;

    const watchingItems = Object.entries(_form)
      .concat(Object.entries(texts))
      .filter((question) => question[1].showIf)
      .map((question) => Object.keys(question[1].showIf)[0]);
    if (watchingItems.length) {
      subscription = watch((data) => {
        const result = {};
        let shouldRerender = false;
        for (const item of watchingItems) {
          const res = data[item]?.key ? data[item].key : data[item];
          Object.assign(result, {
            [item]: res,
          });
          if (res !== forceReRender[item]) shouldRerender = true;
        }
        if (shouldRerender) {
          setForceRerender(result);
        }
      });
    }

    return () => subscription?.unsubscribe();
  }, [forceReRender, _form]);

  const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
    log.error('Failed submit:', errorInfo);

    for (const error of errorInfo.errorFields) {
      const requiredField = error.errors.find((e) => e.startsWith('Please enter'));
      if (requiredField) {
        const index = error.errors.indexOf(requiredField);
        if (index >= 0) {
          setError(String(error.name[index]), {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            message: 'Required field',
          });
        }
      }
    }
  };

  const { t } = useTranslation(['common', 'assets', 'forms_assets', 'forms_contacts']);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const component = useMemo(() => {
    const formItems = Object.entries<ValueType<QuestionType, typeof form>>(_form);

    const _texts = Object.entries(texts);

    const items = [...formItems, ..._texts].sort((a, b) =>
      !(typeof a[1]?.order === 'number' && !b[1]?.order)
        ? Number(a[1]?.order) - Number(b[1]?.order)
        : -100,
    );

    //"renderItems" rendered inside RENDER_NEW_BUTTON, so we don't need to render it here
    const skippedQuestions = items
      .filter(([, item]) => item.type === QuestionType.RENDER_NEW_BUTTON)
      .map(
        ([, item]) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          (item as ValueType<QuestionType.RENDER_NEW_BUTTON, typeof form>).renderItems as string[],
      )
      .flat();

    const formDeleteOptions = items.reduce<
      Record<string, RenderQuestionComponentProps['deleteOptions']>
    >((deleteOptions, [, item]) => {
      if (item.type === QuestionType.RENDER_NEW_BUTTON) {
        deleteOptions[item.deleteOptions.mainFieldName] = {
          ...item.deleteOptions,
          deletedFields: item.renderItems,
        };
      }

      return deleteOptions;
    }, {});

    return (
      <Form onFinish={handleSubmit(onSubmit)} onFinishFailed={onFinishFailed} name={'basic'}>
        <Row nowrap direction="column" gapRow={16}>
          {items.map(([dtoKey, item]) => {
            if (!isLabels(item.type)) {
              const _item = item as ValueType<QuestionType, TDtoWrapper<TDto, THelp>>;

              if (skippedQuestions.includes(dtoKey)) {
                return null;
              }

              const { item: itemOriginalKey } = parseAddNewFieldName(dtoKey);

              const itemDeleteOptions = formDeleteOptions[itemOriginalKey];

              const componentProps: RenderQuestionComponentProps = {
                question: _item,
                control: control,
                id: dtoKey,
                t: t,
                memoizedValue: memoizedValue,
                form: _form,
                setValue: setValue,
                deleteOptions: itemDeleteOptions,
              };

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const shouldRender = !_item.showIf || checkCondition2(forceReRender, _item.showIf);

              return shouldRender ? <RenderQuestionComponent {...componentProps} /> : null;
            } else {
              const _item = item as FormItemLabelType<unknown>;
              const shouldRender = !_item.showIf || checkCondition2(forceReRender, _item.showIf);

              return shouldRender ? renderText(_item, dtoKey, t) : null;
            }
          })}
        </Row>

        {withSaveButton && (
          <>
            <Divider />
            <Row alignCenter gap={16} style={{ marginInline: 0 }}>
              {onSave && !isLastStep && (
                <Button
                  style={{ flex: 1 }}
                  text={t('common:save')}
                  type="default"
                  htmlType="button"
                  onClick={handleSubmit(onSaveSubmit)}
                />
              )}
              <Button
                text={
                  isLastStep === undefined || isLastStep ? t('common:save') : t('common:continue')
                }
                type="primary"
                htmlType="submit"
                disabled={disabled}
                icon={!isLastStep && onSave ? <ArrowRightOutlined /> : null}
                style={{ flex: 1 }}
                iconPosition="end"
              />
            </Row>
          </>
        )}
        <Divider />

        {/* {withDelegation ? (
          <Container marginTop={24}>
            <DelegateButton onClick={handleSubmit(onDelegationSubmit)} withoutCreation />
          </Container>
        ) : null} */}
      </Form>
    );
  }, [_form, forceReRender, withSaveButton]);

  return useMemo(
    () => ({ component, setFormValue: setValue, getValues, reset, isDirty, trigger, handleSubmit }),
    [component, setValue, getValues, reset, isDirty, trigger, handleSubmit],
  );
};

const renderText = (data: FormItemLabelType<unknown>, key: string, t: (key: string) => string) => {
  const { label } = data;

  if (data.type === FormLabelItem.h1) {
    return (
      <div style={{ display: 'flex' }}>
        <Typography.Title level={1} key={key}>
          {t(label)}
        </Typography.Title>
        {data.tooltip && <Tooltip title={t(data.tooltip)} />}
      </div>
    );
  }
  if (data.type === FormLabelItem.text) {
    return (
      <div style={{ display: 'flex' }}>
        <Typography.Text key={key}>{t(label)}</Typography.Text>
        {data.tooltip && <Tooltip title={t(data.tooltip)} />}
      </div>
    );
  }
  if (data.type === FormLabelItem.label) {
    return (
      <div style={{ display: 'flex' }}>
        <Typography.Title level={5} key={key}>
          {t(label)}
        </Typography.Title>
        {data.tooltip && <Tooltip title={t(data.tooltip)} />}
      </div>
    );
  }

  log.error('Unknown text type!', data);

  return null;
};

const getRequiredRule = (question: ValueType<QuestionType, unknown>) =>
  question.required ? 'Field is required' : false;

type RenderQuestionComponentProps = {
  question: ValueType<QuestionType, unknown>;
  control: Control;
  id: string;
  t: (key: string) => string;
  memoizedValue: MutableRefObject<Record<string, unknown>>;
  form: Record<string, unknown>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  deleteOptions: RenderNewButtonDeleteOptions & { deletedFields: string[] };
};

const COUNTRY_FIELDS = [
  'country',
  'assignedCountry',
  'countryOfAcquisition',
  'countryOfOrigin',
  'selectedCountry',
  'citizenship',
  'countryOfBirth',
];

// eslint-disable-next-line complexity
const RenderQuestionComponent: FC<RenderQuestionComponentProps> = ({
  question,
  control,
  id,
  t,
  memoizedValue,
  form,
  setValue,
  deleteOptions,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const sjdb = useSJDatabase();

  const onDelete = (fieldName: string) => {
    const { id: _id, postfix } = parseAddNewFieldName(fieldName);

    deleteOptions.deletedFields.forEach((_fieldName) => {
      const deletedFieldName = getAddNewFieldName({ id: _id, item: _fieldName, postfix: postfix });

      setValue(deletedFieldName, undefined);
      memoizedValue.current[deletedFieldName] = undefined;

      delete form[deletedFieldName];
    });
  };

  const name = (postfix = '') => (postfix ? `${id}${separator}${postfix}` : `${id}`);
  // #region QT PHONE_NUMBER
  if (question.type === QuestionType.PHONE_NUMBER) {
    const { label } = question;

    const memoData =
      typeof memoizedValue.current[id] === 'string'
        ? (memoizedValue.current[id] as string)?.split(' ')
        : undefined;
    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <PhoneNumberWrapper>
          <Controller
            name={name('Dropdown')}
            control={control}
            rules={{ required: getRequiredRule(question) }}
            render={({ field, fieldState }) => {
              return (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <DropdownPhoneNumber
                    showSearch
                    placeholder={t('common:phoneCode')}
                    options={PhoneCodesListForm}
                    defaultValue={memoData?.[0]}
                    {...field}
                  />
                </Form.Item>
              );
            }}
          />
          <Controller
            name={name('Input')}
            control={control}
            rules={{ required: getRequiredRule(question) }}
            render={({ field, fieldState }) => {
              return (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input
                    placeholder={t(label)}
                    type={'number'}
                    defaultValue={memoData?.[1]}
                    {...field}
                  />
                </Form.Item>
              );
            }}
          />
        </PhoneNumberWrapper>
      </div>
    );
  }

  // #region QT TIME_DURATION
  if (question.type === QuestionType.TIME_DURATION) {
    const { label } = question;

    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Controller
          name={name('Dropdown')}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Dropdown
                  placeHolder={t(label)}
                  items={question.items}
                  defaultValue={memoizedValue.current[id] as string}
                  {...field}
                />
              </Form.Item>
            );
          }}
        />
        <Controller
          name={name('Input')}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input placeholder={t(label)} type={'number'} {...field} />
              </Form.Item>
            );
          }}
        />
      </div>
    );
  }
  // TODO Unused
  // #region QT CURRENCY_AMOUNT
  if (question.type === QuestionType.CURRENCY_AMOUNT) {
    const { label } = question;

    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Space size={'large'}>
          <Controller
            name={name('Dropdown')}
            control={control}
            rules={{ required: getRequiredRule(question) }}
            render={({ field, fieldState }) => {
              return (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Dropdown
                    placeHolder={t(label)}
                    items={question.items}
                    defaultValue={memoizedValue.current[id] as string}
                    {...field}
                  />
                </Form.Item>
              );
            }}
          />
          <Controller
            name={name('Input')}
            control={control}
            rules={{ required: getRequiredRule(question) }}
            render={({ field, fieldState }) => {
              return (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input placeholder={t(label)} type={'number'} {...field} />
                </Form.Item>
              );
            }}
          />
        </Space>
      </div>
    );
  }

  const { label } = question;

  // #region QT SELECT_FROM_LIST
  if (question.type === QuestionType.SELECT_FROM_LIST) {
    const { withSearch, items } = question;

    const getSelectOptions = () => {
      return items.map((item) => ({
        value: item.key,
        label: COUNTRY_FIELDS.includes(name()) ? resolveCountry(item.key, false) : t(item.label),
      }));
    };

    const countriesFilterOption: FilterFunc<{
      label: ReactNode;
      value?: string | number | null;
    }> = (inputValue, option) => {
      if (React.isValidElement(option?.label)) {
        const labelText = String(option?.label.props.children[1].props.children);
        return labelText.toLowerCase().includes(inputValue.toLowerCase());
      }
      return false;
    };

    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Controller
          name={name()}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Select
                  showSearch={withSearch}
                  filterOption={countriesFilterOption}
                  placeholder={t(label)}
                  options={getSelectOptions()}
                  style={{ width: '100%' }}
                  mode={question.mode}
                  defaultValue={memoizedValue.current[id] as string}
                  {...field}
                  onChange={(...props) => {
                    question?.onChange?.(field.name, memoizedValue.current, props[0], setValue);
                    return field.onChange(...props);
                  }}
                />
              </Form.Item>
            );
          }}
        />
      </div>
    );
  }

  // #region QT FILE_UPLOAD
  if (question.type === QuestionType.FILE_UPLOAD) {
    return (
      <FileUploading
        id={id}
        question={question}
        label={t(label)}
        control={control}
        memoizedValue={memoizedValue}
        t={t}
        name={name()}
        setValue={setValue}
        key={id}
      />
    );
  }

  if (isDefaultInput(question.type)) {
    const inputType = DefaultInputToInputType[question.type];

    return (
      <InputComponent
        inputType={inputType}
        control={control}
        name={name()}
        question={question}
        label={t(label)}
        placeholder={question.placeholder || t(label)}
        t={t}
        setValue={setValue}
        id={id}
        key={id}
        memoizedValue={memoizedValue}
        deps={question.deps as Array<string>}
        collection={question.collection}
      />
    );
  }

  // #region QT DATE_TIME
  if (question.type === QuestionType.DATE_TIME) {
    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Controller
          name={name()}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => (
            <Form.Item
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              {/* <Input type={'date'} placeholder={label} {...field} /> */}
              <DatePicker
                {...field}
                onChange={(...props) => {
                  // return field.onChange(props[0]?.toISOString?.() ?? null);
                  return field.onChange(props[0]);
                }}
              />
            </Form.Item>
          )}
        />
      </div>
    );
  }

  // #region QT TEXT_MULTILINE
  if (question.type === QuestionType.TEXT_MULTILINE) {
    const { TextArea } = AntInput;
    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Controller
          name={name()}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <TextArea placeholder={t(label)} rows={TextAreaDefaultRowsCount} {...field} />
              </Form.Item>
            );
          }}
        />
      </div>
    );
  }

  // #region QT CHECKBOX
  if (question.type === QuestionType.CHECKBOX) {
    return (
      <Row key={id} justifyContent={'space-between'}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {t(label)} {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        </span>

        <Controller
          name={name()}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Switch checked={field.value as boolean} {...field} />
              </Form.Item>
            );
          }}
        />
      </Row>
    );
  }

  // #region QT RADIO_BUTTON
  if (question.type === QuestionType.RADIO_BUTTON) {
    return (
      <div key={id}>
        <span>{t(label)}</span>
        {question.tooltip && <Tooltip title={t(question.tooltip)} />}
        <Controller
          name={name()}
          control={control}
          rules={{ required: getRequiredRule(question) }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
                valuePropName="checked"
              >
                <Radio checked={memoizedValue.current[id] as boolean} {...field} />
              </Form.Item>
            );
          }}
        />
      </div>
    );
  }

  // #region QT PICK_ARRAY_OF_DTO
  if (question.type === QuestionType.PICK_ARRAY_OF_DTO) {
    const collections = Array.isArray(question.items) ? question.items : [question.items];
    let dtoFilter: PickDtoFilter | undefined;

    if (question.makeExtFilter || question.filter) {
      dtoFilter = question.makeExtFilter
        ? question.makeExtFilter({
            fieldName: name(),
            ctxEntity: memoizedValue.current,
            sjdb: sjdb,
          }) ?? undefined
        : question.filter ??
          ((item) => {
            return !!item;
          });
    }

    return (
      <PickArrayOfDto
        label={t(label)}
        required={question.required}
        control={control}
        collections={collections}
        name={name()}
        mode={question.mode}
        tooltip={question.tooltip}
        memoValue={memoizedValue}
        // deps={question.deps as Array<string>}
        type={question.dtoType ?? FAST_ADD_COMMON_ITEM}
        setValue={setValue}
        filter={dtoFilter}
        isRequired={question.isRequired}
        onValueChange={question.onChange}
        key={id}
        deleteOptions={deleteOptions}
        onDelete={onDelete}
      />
    );
  }

  // #region QT SELECT_PID_BENEFICIARY
  if (question.type === QuestionType.SELECT_PID_BENEFICIARY) {
    return (
      <SelectPidBeneficiary
        label={t(label)}
        required={question.required}
        control={control}
        // collections={collections}
        name={name()}
        // mode={question.mode}
        // tooltip={question.tooltip}
        memoValue={memoizedValue}
        // deps={question.deps as Array<string>}
        // type={question.dtoType ?? COMMON_ITEM}
        setValue={setValue}
        // filter={dtoFilter}
        // isRequired={question.isRequired}
        // onValueChange={question.onChange}
        key={id}
      />
    );
  }

  // #region QT RENDER_NEW_BUTTON
  if (question.type === QuestionType.RENDER_NEW_BUTTON) {
    return (
      <AddNew
        question={question}
        control={control}
        memoValue={memoizedValue}
        form={form}
        tooltip={question.tooltip}
        key={id}
        id={id}
      />
    );
  }

  log.error('Unknown question type!', question);

  return null;
};

export const getAddNewFieldName = ({
  id,
  item,
  postfix,
}: {
  id: string;
  item: string;
  postfix: string;
}) => {
  return `${ADD_NEW_PREFIX}${separator}${id}${separator}${item}${separator}${postfix}`;
};

export const parseAddNewFieldName = (fieldName: string) => {
  const [, id, item, postfix] = fieldName.split(separator);

  return { id, item, postfix };
};

// #region Component AddNew
const AddNew = memo(
  ({
    question,
    form,
    tooltip,
    id,
  }: {
    question: {
      type: QuestionType.RENDER_NEW_BUTTON;
      label: string;
      renderItems: string[];
      uniqKeyPrefix: string;
    };
    control: Control;
    memoValue: MutableRefObject<Record<string, unknown>>;
    form: Record<string, unknown>;
    tooltip?: string;
    id: string;
  }) => {
    const { t } = useTranslation(['common', 'assets', 'forms_assets', 'forms_contacts']);

    const onClick = useCallback(() => {
      const uniqPostfix = nanoid();

      for (const item of question.renderItems) {
        form[getAddNewFieldName({ id, item, postfix: uniqPostfix })] = form[item];
      }
    }, [question]);

    useEffect(() => {
      const hasNewItemFields = Object.keys(form).some((key) => key.startsWith(ADD_NEW_PREFIX));
      if (hasNewItemFields) {
        return;
      }

      const uniqPostfix = nanoid();

      for (const item of question.renderItems) {
        form[getAddNewFieldName({ id, item, postfix: uniqPostfix })] = form[item];
      }
    }, []);

    return (
      <Row>
        <Button text={t(question.label)} onClick={onClick} />
        {tooltip && <Tooltip title={t(tooltip)} />}
      </Row>
    );
  },
);

AddNew.displayName = 'AddNewFormItem';
// #region InputComponent
const InputComponent = ({
  name,
  question,
  t,
  id,
  label,
  placeholder,
  control,
  inputType,
  setValue,
  memoizedValue,
  deps,
  collection,
}: {
  name: string;
  question: ValueType<QuestionType, unknown>;
  t: (text: string) => string;
  id: string;
  label: string;
  placeholder: string;
  control: Control;
  inputType: string;
  setValue: UseFormSetValue<Record<string, unknown>>;
  memoizedValue: MutableRefObject<Record<string, unknown>>;
  deps?: Array<string>;
  collection?: CollectionName;
}) => {
  const isValueSetted = useRef(false);
  const sjDb = useSJDatabase();

  useEffect(() => {
    if (memoizedValue.current[name] && !control._formValues[name]) {
      setValue(name, memoizedValue.current[name]);
    }
  }, [memoizedValue.current, name]);

  const emailValidation = (value: string) => {
    if (collection) {
      const isEmailExist = sjDb.collections[collection as keyof ICollections]?.findOne(
        (item: { email?: string }) => {
          return control._formValues.email === item.email;
        },
      );

      if (isEmailExist) {
        return 'Email already exist';
      }
    }

    return !EMAIL_REGEX.test(value) ? 'Email is invalid' : true;
  };

  const compileDepsFunction = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (value: unknown, formValues: Record<string, unknown>) => {
      if (!deps?.length) return true;

      const realNameArr = name.split(separator);

      if (realNameArr.length === 1) {
        return isEmpty(value) &&
          !isEmpty(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formValues[deps[0]],
          )
          ? 'Required field'
          : true;
      } else {
        return isEmpty(value) &&
          !isEmpty(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formValues[realNameArr[0]]?.[realNameArr[1]]?.[deps[0]]?.[realNameArr[3]],
          )
          ? 'Required field'
          : true;
      }
    },
    [deps, name],
  );

  const onInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldFn: ChangeEventHandler<HTMLInputElement>,
  ) => {
    memoizedValue.current[name] = e.target.value;
    fieldFn(e);
  };

  const commonInputProps = useMemo(() => {
    if (inputType === 'number') {
      return {
        // Disable change on scroll
        onWheel: (ev: WheelEvent<HTMLInputElement>) => ev.currentTarget.blur(),
      };
    } else {
      return {};
    }
  }, [inputType]);

  return (
    <div key={id}>
      <span>{t(label)}</span>
      {question.tooltip && <Tooltip title={t(question.tooltip)} />}
      <Controller
        name={name}
        control={control}
        rules={{
          required: getRequiredRule(question),
          validate: {
            compileDepsFunction: compileDepsFunction,
            ...(inputType === 'email' && question.required && { emailValidation: emailValidation }),
          },
        }}
        render={({ field, fieldState }) => {
          if (!field.value && memoizedValue.current[name] && !isValueSetted.current) {
            field.value = memoizedValue.current[name];
            isValueSetted.current = true;
            setValue(name, memoizedValue.current[name]);
          }
          return (
            <Form.Item
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input
                placeholder={placeholder}
                type={inputType}
                {...field}
                {...commonInputProps}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e, field.onChange)}
              />
            </Form.Item>
          );
        }}
      />
    </div>
  );
};

const PhoneNumberWrapper = styled.div`
  display: grid;
  column-gap: 4px;
  grid-template-columns: 100px auto;
  width: 100%;
`;

const DropdownPhoneNumber = styled(Select)`
  display: grid;
  column-gap: 4px;
  grid-template-columns: 100px auto;
  width: 100%;
`;
